import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../services/api';
import AccountMenu from '../Common/AccountMenu';
import MetricCard from '../Common/MetricCard';
import TabNavigation, { Tab } from '../Common/TabNavigation';
import DataUploadView from '../Admin/DataUploadView';
import CoverageAnalysisView from '../Admin/CoverageAnalysisView';
import VisibilityMetricsView from '../Admin/VisibilityMetricsView';
import UserManagementView from '../Admin/UserManagementView';
import AlertManagementView from '../Admin/AlertManagementView';
import LoginActivityView from '../Admin/LoginActivityView';
import './dashboard-common.css';
import './AdminDashboard.css';

interface SystemStats {
  total_users: number;
  total_dealers: number;
  total_advertising_records: number;
  total_hierarchy_records: number;
  admin_username: string;
  collections: string[];
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchSystemStats();
    }
  }, [activeTab]);

  const fetchSystemStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminAPI.getSystemStats();
      setStats(data);
    } catch (err: any) {
      console.error('Admin stats error:', err);
      if (err.response?.status === 403) {
        setError('Access denied: Admin role required');
      } else if (err.response?.status === 401) {
        setError('Please log in as an admin user');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load system stats');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const getOverviewContent = () => {
    if (loading) {
      return (
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading system statistics...</p>
        </div>
      );
    }

    return (
      <div className="overview-content">
        {error && (
          <div className="error-container">
            <div className="error-message">{error}</div>
          </div>
        )}

        {stats && !loading && (
          <>
            {/* Metrics Grid - matches corporate dashboard */}
            <div className="metrics-grid">
              <MetricCard
                title="TOTAL USERS"
                value={stats.total_users}
                color="#ff6b00"
              />
              <MetricCard
                title="TOTAL DEALERS"
                value={stats.total_dealers}
                color="#2196F3"
              />
              <MetricCard
                title="ADVERTISING RECORDS"
                value={stats.total_advertising_records}
                color="#4CAF50"
              />
              <MetricCard
                title="HIERARCHY RECORDS"
                value={stats.total_hierarchy_records}
                color="#9C27B0"
              />
            </div>

            {/* System Info Card */}
            <div className="system-info-card">
              <h3>System Information</h3>
              <div className="info-row">
                <span className="info-label">Database:</span>
                <span className="info-value">MongoDB Atlas</span>
              </div>
              <div className="info-row">
                <span className="info-label">Collections:</span>
                <span className="info-value">{stats.collections.length} collections</span>
              </div>
              <div className="info-row">
                <span className="info-label">Admin User:</span>
                <span className="info-value">{stats.admin_username}</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const tabs: Tab[] = [
    {
      id: 'overview',
      label: 'System Overview',
      icon: 'dashboard',
      content: getOverviewContent()
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'people',
      content: (
        <div className="tab-content-wrapper">
          <UserManagementView currentUserRole="admin" />
        </div>
      )
    },
    {
      id: 'upload',
      label: 'Upload Data',
      icon: 'upload_file',
      content: (
        <div className="tab-content-wrapper">
          <DataUploadView />
        </div>
      )
    },
    {
      id: 'coverage',
      label: 'Coverage Analysis',
      icon: 'analytics',
      content: (
        <div className="tab-content-wrapper">
          <CoverageAnalysisView />
        </div>
      )
    },
    {
      id: 'visibility',
      label: 'Visibility Metrics',
      icon: 'visibility',
      content: (
        <div className="tab-content-wrapper">
          <VisibilityMetricsView />
        </div>
      )
    },
    {
      id: 'alerts',
      label: 'Alert Messages',
      icon: 'campaign',
      content: (
        <div className="tab-content-wrapper">
          <AlertManagementView />
        </div>
      )
    },
    {
      id: 'activity',
      label: 'Login Activity',
      icon: 'login',
      content: (
        <div className="tab-content-wrapper">
          <LoginActivityView />
        </div>
      )
    }
  ];

  return (
    <div className="dashboard-container admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logos">
              <img src="/img/gravely_new.svg" alt="Gravely" className="header-logo gravely-logo" />
              <img src="/img/ariens.svg" alt="Ariens" className="header-logo ariens-logo" />
            </div>
            <div className="header-title-section">
              <h1 className="header-title">Admin Dashboard</h1>
              <div className="header-subtitle">
                <span className="user-name">{user?.username || 'Admin User'}</span>
                <span className="header-badge secondary">System Administration</span>
              </div>
            </div>
          </div>
          <div className="header-right">
            <AccountMenu />
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </main>
    </div>
  );
};

export default AdminDashboard;
